import { useTranslation } from "react-i18next";
import { Flex } from "../../../../components/Flex";
import { Text } from "../../../../components/Text";
import { useAppDispatch, useAppSelector } from "../../../../redux/store";
import { entryActions } from "../../../../redux/entry/entry.slice";
import { AtiraToast } from "../../../../utils/AtiraToast";
import { entrySliceSelectors } from "../../../../redux/entry/entry.selector";
import type { CheckboxOptionType, GetProp } from "../../../../utils/local-antd";
import { Checkbox } from "../../../../utils/local-antd";
import { useEffect, useState } from "react";
import { UpdateUserEntriesTableSettingsDto } from "../../../../model/entries-table-settings/dto/UpdateUserEntriesTableSettingsDto";import { userSliceSelectors } from "../../../../redux/user/user.selector";
import { styled } from "styled-components";
import { Button } from "../../../../components/Button";
import { AtiraCheckBoxGroup } from "../../../../components/AtiraCheckBoxGroup";
import { Info } from "../../../../components/Info";
import { canHideTableColumn } from "../../utils/tables.util";

const StyledButton = styled(Button)`
  width: 7rem;
  height: 2.5rem;
  padding: 0;
`;

type Props = {
  open?: boolean;
};

export const EntryTableSettingsColumns: React.FC<Props> = ({ open }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [userOptions, setUserOptions] = useState<string[]>([]);
  const [updateColumnsLoading, setUpdateColumnsLoading] = useState(false);

  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId)!;
  const tableSettings = useAppSelector(
    entrySliceSelectors.selectUserEntryTableSettings,
  );

  const allOptions: CheckboxOptionType[] = (tableSettings?.columns || [])
    .map((c) => ({
      label: c.custom ? c.label : t(`entries.table.settings.column.${c.field}`),
      value: c.field,
      disabled: !canHideTableColumn(c),
    }))
    .sort((a, b) => a.label?.localeCompare(b.label));

  const onCheckBoxChange: GetProp<typeof Checkbox.Group, "onChange"> = (
    checkedValues,
  ) => {
    setUserOptions(checkedValues as string[]);
  };

  const getUpdateDto = (): UpdateUserEntriesTableSettingsDto => {
    return {
      tableId: tableSettings?._id!,
      userId,
      // Custom fields are named on their column's header, so they are kept
      // exactly as they are here
      columns: tableSettings?.columns
        .filter((f) => f.field !== "assignee")
        .map((col) => ({
          ...col,
          enabled: userOptions.includes(col.field),
        })),
    };
  };

  const onSaveColumns = async () => {
    try {
      setUpdateColumnsLoading(true);

      await dispatch(
        entryActions.updateUserTableSettings(getUpdateDto()),
      ).unwrap();
      await dispatch(entryActions.getUserTableSettings({ userId })).unwrap();

      AtiraToast.success(t("entries.table.settings.columns.button.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
    } finally {
      setUpdateColumnsLoading(false);
    }
  };

  useEffect(() => {
    const _userOptions = (tableSettings?.columns || [])
      ?.filter(({ enabled }) => enabled)
      .map(({ field }) => field);

    setUserOptions(_userOptions);
    // Do not remove `open`
    // We need to update the userOptions when the modal is opened to keep the state in sync
  }, [tableSettings?.columns, open]);

  return (
    <Flex gap="xl" flexDirection="column">
      <Flex flexDirection="column" gap="s">
        <Text fontWeight={"bold"}>
          {t("entries.table.settings.columns.title")}
        </Text>
        <Info info={t("entries.table.settings.columns.description")} />
      </Flex>

      <AtiraCheckBoxGroup
        columnGap="7rem"
        options={allOptions}
        value={userOptions}
        onChange={onCheckBoxChange}
      />

      <StyledButton loading={updateColumnsLoading} onClick={onSaveColumns}>
        {t("common.update")}
      </StyledButton>
    </Flex>
  );
};
